import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminUsersAPI, adminMatchesAPI } from '../../lib/admin-api';
import { useStore } from '../../store/useStore';
import { useAdminStore } from '../../store/adminStore';

type SortField = 'id' | 'username' | 'email' | 'gender' | 'plan_type' | 'is_active' | 'created_at';
type SortOrder = 'asc' | 'desc';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { setUser, setToken } = useStore();
  const adminToken = useAdminStore((state) => state.token);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [impersonating, setImpersonating] = useState<number | null>(null);
  const searchTimeoutRef = useRef<number | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = window.setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    loadUsers();
  }, [debouncedSearch]);

  const loadUsers = () => {
    setLoading(true);
    adminUsersAPI.getUsers({ limit: 100, search: debouncedSearch })
      .then((data) => {
        console.log('[AdminUsers] Loaded users:', data.users.length);
        setUsers(data.users);
      })
      .catch((error) => {
        console.error('[AdminUsers] Failed to load users:', error);
        setUsers([]);
      })
      .finally(() => setLoading(false));
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle null/undefined values
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    // String comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const getRowClassName = (user: any) => {
    let baseClass = '';

    // 성별에 따른 배경색
    if (user.gender === 'male') {
      baseClass = 'bg-blue-50 hover:bg-blue-100';
    } else if (user.gender === 'female') {
      baseClass = 'bg-pink-50 hover:bg-pink-100';
    } else {
      baseClass = 'hover:bg-base-200';
    }

    // 선택된 경우 강조
    if (selectedUsers.includes(user.id)) {
      baseClass += ' ring-2 ring-primary';
    }

    return baseClass;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-base-content/30">⇅</span>;
    }
    return sortOrder === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else if (prev.length < 2) {
        return [...prev, userId];
      } else {
        // 이미 2명 선택됨 - 첫 번째를 제거하고 새로운 것 추가
        return [prev[1], userId];
      }
    });
  };

  const [errorMessage, setErrorMessage] = useState('');

  const handleImpersonate = async (userId: number, username: string) => {
    if (!confirm(`"${username}" 유저로 로그인하시겠습니까?\n\n현재 관리자 세션은 유지되며, 유저 세션으로 전환됩니다.`)) {
      return;
    }

    setImpersonating(userId);
    try {
      // adminToken from Zustand store or fallback to localStorage
      const token = adminToken || localStorage.getItem('admin_token');
      console.log('[DEBUG] adminToken from store:', adminToken);
      console.log('[DEBUG] admin_token from localStorage:', localStorage.getItem('admin_token'));
      console.log('[DEBUG] final token:', token);

      if (!token) {
        throw new Error('관리자 토큰이 없습니다. 다시 로그인해주세요.');
      }

      const response = await fetch(`/api/admin/users/${userId}/impersonate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to impersonate user');
      }

      const data = await response.json();

      // 유저 토큰과 정보로 전환
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);

      setSuccessMessage(`✅ "${data.user.username}"님으로 로그인했습니다. 메인 페이지로 이동합니다...`);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error: any) {
      console.error('[AdminUsers] Impersonate error:', error);
      setErrorMessage(`❌ 유저 로그인 실패: ${error.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setImpersonating(null);
    }
  };

  const handleCreateMatch = async () => {
    if (selectedUsers.length !== 2) {
      setErrorMessage('정확히 2명의 사용자를 선택해주세요.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setCreating(true);
    setErrorMessage('');

    try {
      const user1 = users.find(u => u.id === selectedUsers[0]);
      const user2 = users.find(u => u.id === selectedUsers[1]);

      console.log('[AdminUsers] Creating match:', {
        user1: { id: selectedUsers[0], username: user1?.username, found: !!user1 },
        user2: { id: selectedUsers[1], username: user2?.username, found: !!user2 }
      });

      if (!user1 || !user2) {
        throw new Error('선택한 사용자를 찾을 수 없습니다. 페이지를 새로고침해주세요.');
      }

      const payload = {
        user_id: selectedUsers[0],
        target_user_id: selectedUsers[1],
        priority: 10,
        reason: 'Admin created match from user selection',
      };

      console.log('[AdminUsers] Sending match payload:', payload);

      const result = await adminMatchesAPI.createForcedMatch(payload);

      console.log('[AdminUsers] Match created successfully:', result);

      setSuccessMessage(
        `✅ 매칭 생성 완료: ${user1.username} ↔ ${user2.username}`
      );
      setSelectedUsers([]);

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('[AdminUsers] Match creation error:', error);
      const errorMsg = error.message || error.error || '알 수 없는 오류가 발생했습니다.';
      setErrorMessage(`❌ ${errorMsg}`);
      setTimeout(() => setErrorMessage(''), 7000);
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><span className="loading loading-spinner loading-lg"></span></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Users Management</h1>
        {selectedUsers.length === 2 && (
          <button
            className={`btn btn-primary ${creating ? 'loading' : ''}`}
            onClick={handleCreateMatch}
            disabled={creating}
          >
            매칭 생성 ({selectedUsers[0]} ↔ {selectedUsers[1]})
          </button>
        )}
      </div>

      {successMessage && (
        <div className="alert alert-success mb-4">
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-error mb-4">
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="mb-4">
        <input
          type="text"
          placeholder="이름 또는 이메일로 검색..."
          className="input input-bordered w-full max-w-xs"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="text-sm text-base-content/60 mt-2">
          {selectedUsers.length === 0 && '2명의 사용자를 선택하여 매칭을 생성하세요'}
          {selectedUsers.length === 1 && '1명 선택됨 - 1명 더 선택하세요'}
          {selectedUsers.length === 2 && '2명 선택됨 - 매칭 생성 버튼을 클릭하세요'}
        </div>
      </div>

      <div className="overflow-x-auto bg-base-100 rounded-box shadow">
        <table className="table">
          <thead>
            <tr>
              <th>선택</th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('id')}
              >
                ID <SortIcon field="id" />
              </th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('username')}
              >
                Username <SortIcon field="username" />
              </th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('email')}
              >
                Email <SortIcon field="email" />
              </th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('gender')}
              >
                Gender <SortIcon field="gender" />
              </th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('plan_type')}
              >
                Plan <SortIcon field="plan_type" />
              </th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('is_active')}
              >
                Status <SortIcon field="is_active" />
              </th>
              <th
                className="cursor-pointer hover:bg-base-200"
                onClick={() => handleSort('created_at')}
              >
                Created <SortIcon field="created_at" />
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <tr
                key={user.id}
                className={getRowClassName(user)}
              >
                <td>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleUserSelection(user.id)}
                    disabled={!selectedUsers.includes(user.id) && selectedUsers.length >= 2}
                  />
                </td>
                <td>{user.id}</td>
                <td className="font-semibold">{user.username}</td>
                <td>{user.email}</td>
                <td>
                  {user.gender === 'male' ? (
                    <span className="badge badge-info">♂ Male</span>
                  ) : user.gender === 'female' ? (
                    <span className="badge badge-secondary">♀ Female</span>
                  ) : (
                    <span className="badge badge-ghost">-</span>
                  )}
                </td>
                <td><span className="badge">{user.plan_type || 'free'}</span></td>
                <td>
                  {user.is_active ? (
                    <span className="badge badge-success">Active</span>
                  ) : (
                    <span className="badge badge-error">Banned</span>
                  )}
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn btn-xs btn-primary"
                    onClick={() => handleImpersonate(user.id, user.username)}
                    disabled={impersonating === user.id}
                  >
                    {impersonating === user.id ? (
                      <span className="loading loading-spinner loading-xs"></span>
                    ) : (
                      '🔑 로그인'
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
